import React, { useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useTheme } from 'react-native-paper';
import QText from './QText';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { getFontSize, getResponsiveValue } from '../utils';

const GooglePlacesSearchResults = ({
  results,
  onBusinessSelect,
  isLoading = false,
  searchQuery = '',
  onClose,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <QText style={styles.title}>🔍 Searching Google Places...</QText>
          <View style={styles.loadingContainer}>
            <FontAwesome
              name="spinner"
              size={20}
              color={theme.colors.primary}
            />
            <QText style={styles.loadingText}>
              Finding businesses for "{searchQuery}"
            </QText>
          </View>
        </View>
      </View>
    );
  }

  if (!results || results.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <QText style={styles.title}>🗺️ Google Places Results</QText>
          <QText style={styles.subtitle}>
            No businesses found for "{searchQuery}"
          </QText>
        </View>
      </View>
    );
  }

  const getBusinessIcon = (types) => {
    if (!types || types.length === 0) return '🏢';

    const primaryType = types[0];
    const iconMap = {
      hospital: '🏥',
      health: '🏥',
      pharmacy: '💊',
      veterinary_care: '🐾',
      pet_store: '🐕',
      restaurant: '🍽️',
      food: '🍕',
      bank: '🏦',
      school: '🎓',
      university: '🎓',
      store: '🛍️',
      shopping_mall: '🏬',
      gas_station: '⛽',
      car_dealer: '🚗',
      car_rental: '🚗',
      hotel: '🏨',
      lodging: '🏨',
      gym: '💪',
      fitness_center: '💪',
      beauty_salon: '💇‍♀️',
      spa: '🧖‍♀️',
      dentist: '🦷',
      doctor: '👨‍⚕️',
      lawyer: '⚖️',
      real_estate_agency: '🏠',
      insurance_agency: '🛡️',
      post_office: '📮',
      police: '👮‍♂️',
      fire_station: '🚒',
      library: '📚',
      museum: '🏛️',
      park: '🌳',
      amusement_park: '🎡',
      movie_theater: '🎬',
      stadium: '🏟️',
      church: '⛪',
      mosque: '🕌',
      temple: '🕍',
      synagogue: '🕍',
    };

    return iconMap[primaryType] || '🏢';
  };

  const getBusinessCategory = (types) => {
    if (!types || types.length === 0) return 'Business';

    const primaryType = types[0];
    const categoryMap = {
      hospital: 'Healthcare',
      health: 'Healthcare',
      pharmacy: 'Pharmacy',
      veterinary_care: 'Veterinary',
      pet_store: 'Pet Store',
      restaurant: 'Restaurant',
      food: 'Food Service',
      bank: 'Banking',
      school: 'Education',
      university: 'Education',
      store: 'Retail',
      shopping_mall: 'Shopping',
      gas_station: 'Gas Station',
      car_dealer: 'Automotive',
      car_rental: 'Car Rental',
      hotel: 'Hospitality',
      lodging: 'Accommodation',
      gym: 'Fitness',
      fitness_center: 'Fitness',
      beauty_salon: 'Beauty',
      spa: 'Wellness',
      dentist: 'Dental Care',
      doctor: 'Medical',
      lawyer: 'Legal',
      real_estate_agency: 'Real Estate',
      insurance_agency: 'Insurance',
      post_office: 'Postal Service',
      police: 'Law Enforcement',
      fire_station: 'Emergency',
      library: 'Library',
      museum: 'Museum',
      park: 'Park',
      amusement_park: 'Entertainment',
      movie_theater: 'Entertainment',
      stadium: 'Sports',
      church: 'Religious',
      mosque: 'Religious',
      temple: 'Religious',
      synagogue: 'Religious',
    };

    return categoryMap[primaryType] || 'Business';
  };

  const formatOpeningHours = (openingHours) => {
    if (!openingHours || !openingHours.weekdayDescriptions) {
      return 'Hours not available';
    }

    // Show today's hours if available
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    const todayIndex = today === 0 ? 6 : today - 1; // Adjust for array index

    if (openingHours.weekdayDescriptions[todayIndex]) {
      return openingHours.weekdayDescriptions[todayIndex];
    }

    // Fallback to first available day
    return openingHours.weekdayDescriptions[0] || 'Hours not available';
  };

  const getStatusColor = (isOpen) => {
    return isOpen ? '#4CAF50' : '#F44336';
  };

  const getStatusText = (isOpen) => {
    return isOpen ? 'OPEN NOW' : 'CLOSED';
  };

  const handleBusinessSelect = (business) => {
    if (onBusinessSelect) {
      onBusinessSelect(business);
    }
  };

  const handleGetDirections = (business) => {
    const query = encodeURIComponent(`${business.name} ${business.location}`);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
    window.open(mapsUrl, '_blank');
  };

  const handleCallBusiness = (phoneNumber) => {
    if (phoneNumber) {
      const telUrl = `tel:${phoneNumber.replace(/\s/g, '')}`;
      window.open(telUrl, '_self');
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <QText style={styles.title}>🗺️ Google Places Results</QText>
          <QText style={styles.subtitle}>
            Found {results.length} businesses for "{searchQuery}"
          </QText>
        </View>
        {onClose && (
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <FontAwesome
              name="times"
              size={20}
              color={theme.colors.onSurface}
            />
          </TouchableOpacity>
        )}
      </View>

      {/* Results Grid */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.resultsGrid}
        snapToInterval={getResponsiveValue(320, 300, 280)}
        decelerationRate="fast"
      >
        {results.map((business, index) => (
          <View key={business.id || index} style={styles.businessCard}>
            {/* Business Icon and Status */}
            <View style={styles.cardHeader}>
              <View style={styles.businessIconContainer}>
                <QText style={styles.businessIcon}>
                  {getBusinessIcon(business.types)}
                </QText>
              </View>
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: getStatusColor(business.isOpen) },
                ]}
              >
                <QText style={styles.statusText}>
                  {getStatusText(business.isOpen)}
                </QText>
              </View>
            </View>

            {/* Business Info */}
            <View style={styles.businessInfo}>
              <QText style={styles.businessName} numberOfLines={2}>
                {business.name}
              </QText>

              <View style={styles.categoryRow}>
                <QText style={styles.businessCategory}>
                  {getBusinessCategory(business.types)}
                </QText>
                <View style={styles.ratingContainer}>
                  <FontAwesome name="star" size={12} color="#FFD700" />
                  <QText style={styles.ratingText}>
                    {business.rating} ({business.totalReviews})
                  </QText>
                </View>
              </View>

              {/* Address */}
              <View style={styles.addressRow}>
                <FontAwesome
                  name="map-marker"
                  size={14}
                  color={theme.colors.primary}
                />
                <QText style={styles.addressText} numberOfLines={2}>
                  {business.location}
                </QText>
              </View>

              {/* Phone Number */}
              {business.phone && (
                <View style={styles.phoneRow}>
                  <FontAwesome
                    name="phone"
                    size={14}
                    color={theme.colors.primary}
                  />
                  <TouchableOpacity
                    onPress={() => handleCallBusiness(business.phone)}
                    style={styles.phoneButton}
                  >
                    <QText style={styles.phoneText}>{business.phone}</QText>
                  </TouchableOpacity>
                </View>
              )}

              {/* Opening Hours */}
              {business.regularOpeningHours && (
                <View style={styles.hoursRow}>
                  <FontAwesome
                    name="clock-o"
                    size={14}
                    color={theme.colors.primary}
                  />
                  <QText style={styles.hoursText} numberOfLines={1}>
                    {formatOpeningHours(business.regularOpeningHours)}
                  </QText>
                </View>
              )}

              {/* Action Buttons */}
              <View style={styles.actionButtons}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.primaryButton]}
                  onPress={() => handleBusinessSelect(business)}
                >
                  <FontAwesome name="info-circle" size={14} color="#FFFFFF" />
                  <QText style={styles.primaryButtonText}>View Details</QText>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.actionButton, styles.secondaryButton]}
                  onPress={() => handleGetDirections(business)}
                >
                  <FontAwesome
                    name="map"
                    size={14}
                    color={theme.colors.primary}
                  />
                  <QText
                    style={[
                      styles.secondaryButtonText,
                      { color: theme.colors.primary },
                    ]}
                  >
                    Directions
                  </QText>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}
      </ScrollView>
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
      maxWidth: 1200,
      alignSelf: 'center',
      marginTop: getResponsiveValue(40, 30, 20),
      paddingHorizontal: getResponsiveValue(20, 15, 10),
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: getResponsiveValue(20, 15, 10),
      paddingHorizontal: getResponsiveValue(10, 5, 0),
    },
    headerContent: {
      flex: 1,
    },
    title: {
      fontSize: getFontSize(28, 24, 20),
      color: theme.colors.landingText,
      fontFamily: 'Poppins',
      fontWeight: '600',
      marginBottom: getResponsiveValue(8, 6, 4),
    },
    subtitle: {
      fontSize: getFontSize(16, 14, 12),
      color: theme.colors.landingText,
      opacity: 0.7,
      fontFamily: 'Poppins',
    },
    closeButton: {
      padding: getResponsiveValue(8, 6, 4),
      borderRadius: 8,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      minWidth: getResponsiveValue(40, 35, 30),
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        transform: 'scale(1.05)',
      },
    },
    loadingContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      padding: getResponsiveValue(40, 30, 20),
      minHeight: getResponsiveValue(200, 150, 100),
    },
    loadingText: {
      fontSize: getFontSize(16, 14, 12),
      color: theme.colors.landingText,
      fontFamily: 'Poppins',
      marginTop: getResponsiveValue(15, 12, 10),
      opacity: 0.8,
    },
    resultsGrid: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      gap: getResponsiveValue(20, 15, 12),
      paddingHorizontal: getResponsiveValue(10, 5, 0),
      paddingRight: getResponsiveValue(40, 30, 20),
    },
    businessCard: {
      width: getResponsiveValue(320, 300, 280),
      minWidth: getResponsiveValue(320, 300, 280),
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: 16,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      flexShrink: 0,
      ':hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
      },
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: getResponsiveValue(16, 14, 12),
      paddingBottom: getResponsiveValue(12, 10, 8),
    },
    businessIconContainer: {
      width: getResponsiveValue(50, 45, 40),
      height: getResponsiveValue(50, 45, 40),
      borderRadius: getResponsiveValue(25, 22, 20),
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    businessIcon: {
      fontSize: getResponsiveValue(24, 22, 20),
    },
    statusBadge: {
      paddingHorizontal: getResponsiveValue(8, 6, 4),
      paddingVertical: getResponsiveValue(4, 3, 2),
      borderRadius: 8,
    },
    statusText: {
      fontSize: getFontSize(10, 8, 6),
      color: '#FFFFFF',
      fontFamily: 'Poppins',
      fontWeight: '600',
    },
    businessInfo: {
      padding: getResponsiveValue(16, 14, 12),
      paddingTop: 0,
    },
    businessName: {
      fontSize: getFontSize(18, 16, 14),
      color: theme.colors.landingText,
      fontFamily: 'Poppins',
      fontWeight: '600',
      marginBottom: getResponsiveValue(8, 6, 4),
      lineHeight: getResponsiveValue(22, 20, 18),
    },
    categoryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: getResponsiveValue(12, 10, 8),
    },
    businessCategory: {
      fontSize: getFontSize(12, 10, 8),
      color: theme.colors.landingGreen,
      fontFamily: 'Poppins',
      fontWeight: '500',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      paddingHorizontal: getResponsiveValue(8, 6, 4),
      paddingVertical: getResponsiveValue(4, 3, 2),
      borderRadius: 12,
    },
    ratingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: getResponsiveValue(4, 3, 2),
    },
    ratingText: {
      fontSize: getFontSize(12, 10, 8),
      color: theme.colors.landingText,
      fontFamily: 'Poppins',
      fontWeight: '500',
    },
    addressRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: getResponsiveValue(8, 6, 4),
      marginBottom: getResponsiveValue(8, 6, 4),
    },
    addressText: {
      fontSize: getFontSize(12, 10, 8),
      color: theme.colors.landingText,
      opacity: 0.8,
      fontFamily: 'Poppins',
      flex: 1,
      lineHeight: getResponsiveValue(16, 14, 12),
    },
    phoneRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: getResponsiveValue(8, 6, 4),
      marginBottom: getResponsiveValue(8, 6, 4),
    },
    phoneButton: {
      flex: 1,
    },
    phoneText: {
      fontSize: getFontSize(12, 10, 8),
      color: theme.colors.landingGreen,
      fontFamily: 'Poppins',
      fontWeight: '500',
      textDecorationLine: 'underline',
    },
    hoursRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: getResponsiveValue(8, 6, 4),
      marginBottom: getResponsiveValue(16, 14, 12),
    },
    hoursText: {
      fontSize: getFontSize(12, 10, 8),
      color: theme.colors.landingText,
      opacity: 0.7,
      fontFamily: 'Poppins',
      flex: 1,
    },
    actionButtons: {
      flexDirection: 'row',
      gap: getResponsiveValue(8, 6, 4),
    },
    actionButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: getResponsiveValue(6, 4, 2),
      paddingVertical: getResponsiveValue(8, 6, 4),
      paddingHorizontal: getResponsiveValue(12, 10, 8),
      borderRadius: 8,
      flex: 1,
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      ':hover': {
        transform: 'scale(1.02)',
      },
    },
    primaryButton: {
      backgroundColor: theme.colors.landingGreen,
    },
    primaryButtonText: {
      color: '#FFFFFF',
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      fontWeight: '500',
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    secondaryButtonText: {
      fontSize: getFontSize(12, 10, 8),
      fontFamily: 'Poppins',
      fontWeight: '500',
    },
  });

export default GooglePlacesSearchResults;
